import React from 'react';
import { FilterBarProps, ResolvedFilterBarProps } from './types';
import { useFilterBar } from './useFilterBar';
import { FilterSelectGroup } from './FilterSelectGroup';
import { FilterTogglesGroup } from './FilterTogglesGroup';
import { FilterBarResetButton } from './FilterBarResetButton';
import { useUI } from '../../contexts/UIContext';

export const FilterBar: React.FC<FilterBarProps> = (props) => {
  const ui = useUI();

  const resolved: ResolvedFilterBarProps = {
    show: props.show,
    filterAssignee: props.filterAssignee ?? ui.filterAssignee,
    onFilterAssignee: props.onFilterAssignee ?? ui.setFilterAssignee,
    filterType: props.filterType ?? ui.filterType,
    onFilterType: props.onFilterType ?? ui.setFilterType,
    filterStatus: props.filterStatus ?? ui.filterStatus,
    onFilterStatus: props.onFilterStatus ?? ui.setFilterStatus,
    filterPriority: props.filterPriority ?? ui.filterPriority,
    onFilterPriority: props.onFilterPriority ?? ui.setFilterPriority,
    showDependencies: props.showDependencies ?? ui.showDependencies,
    onShowDependencies: props.onShowDependencies ?? ui.setShowDependencies,
    hideEmptyDays: props.hideEmptyDays ?? ui.hideEmptyDays,
    onHideEmptyDays: props.onHideEmptyDays ?? ui.setHideEmptyDays,
    datePadding: props.datePadding ?? ui.datePadding,
    onDatePadding: props.onDatePadding ?? ui.setDatePadding,
    allAssignees: props.allAssignees ?? ui.allAssignees,
    showCompleted: props.showCompleted ?? ui.showCompleted,
    onToggleShowCompleted: props.onToggleShowCompleted ?? ui.setShowCompleted,
    showArchived: props.showArchived ?? ui.showArchived,
    onToggleShowArchived: props.onToggleShowArchived ?? ui.setShowArchived,
    showTaskDetails: props.showTaskDetails ?? ui.showTaskDetails,
    onToggleShowTaskDetails: props.onToggleShowTaskDetails ?? ui.setShowTaskDetails,
    topOffset: props.topOffset ?? 64,
    isSidebarCollapsed: props.isSidebarCollapsed ?? ui.isSidebarCollapsed,
    isRightSidebarOpen: props.isRightSidebarOpen ?? false,
  };

  const {
    show,
    filterAssignee,
    onFilterAssignee,
    filterType,
    onFilterType,
    filterStatus,
    onFilterStatus,
    filterPriority,
    onFilterPriority,
    showDependencies,
    onShowDependencies,
    hideEmptyDays,
    onHideEmptyDays,
    datePadding,
    onDatePadding,
    allAssignees,
    showCompleted,
    onToggleShowCompleted,
    showArchived,
    onToggleShowArchived,
    showTaskDetails,
    onToggleShowTaskDetails,
    topOffset = 64,
    isSidebarCollapsed = false,
    isRightSidebarOpen = false,
  } = resolved;

  const { isAnyFilterActive, handleResetFilters } = useFilterBar(resolved);

  if (!show) return null;

  const sidebarWidth = isSidebarCollapsed ? '80px' : '320px';
  const rightSidebarWidth = isRightSidebarOpen ? '80px' : '0px';

  return (
    <div
      className="gwantt-filterbar bg-white/95 dark:bg-slate-900/95 backdrop-blur-md px-5 h-[44px] border-b border-slate-200 dark:border-slate-800 flex items-center gap-5 overflow-x-auto animate-in slide-in-from-top duration-300 shadow-sm sticky z-30 transition-all"
      style={{
        top: `${topOffset}px`,
        left: sidebarWidth,
        width: `calc(100% - ${sidebarWidth} - ${rightSidebarWidth})`,
        marginLeft: sidebarWidth,
        marginRight: rightSidebarWidth
      }}
    >
      <div className="flex-1 flex items-center justify-start gap-5 overflow-x-auto custom-scrollbar px-2">
        <FilterSelectGroup
          filterAssignee={filterAssignee}
          onFilterAssignee={onFilterAssignee}
          allAssignees={allAssignees}
          filterType={filterType}
          onFilterType={onFilterType}
          filterStatus={filterStatus}
          onFilterStatus={onFilterStatus}
          filterPriority={filterPriority}
          onFilterPriority={onFilterPriority}
          datePadding={datePadding}
          onDatePadding={onDatePadding}
        />

        <div className="h-5 w-px bg-slate-200 dark:bg-slate-800" />

        <FilterTogglesGroup
          showDependencies={showDependencies}
          onShowDependencies={onShowDependencies}
          hideEmptyDays={hideEmptyDays}
          onHideEmptyDays={onHideEmptyDays}
          showCompleted={showCompleted}
          onToggleShowCompleted={onToggleShowCompleted}
          showArchived={showArchived}
          onToggleShowArchived={onToggleShowArchived}
          showTaskDetails={showTaskDetails}
          onToggleShowTaskDetails={onToggleShowTaskDetails}
        />
      </div>

      {isAnyFilterActive && (
        <FilterBarResetButton onReset={handleResetFilters} />
      )}
    </div>
  );
};

export default React.memo(FilterBar);
